// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The exported artefacts.
 *
 * The report is the part of this tool that leaves the building. It gets
 * forwarded to a letting agent, an HR partner, a mortgage broker — stripped of
 * every caveat the person read on screen and did not export. So the caveats
 * are written INTO the file, at the top, uncollapsed, before any finding. A
 * warning that lives only in the interface is a warning that reaches nobody
 * who matters.
 *
 * There is also an applicant-facing version, written in the second person. A
 * landlord or employer looking at a document handed to them directly is not a
 * credit-reporting agency, so none of the machinery that normally gives a
 * person the right to see and answer a decision applies to them. That is a gap
 * this tool would otherwise widen, and the cheapest possible fix is to make the
 * explaining-it version one click away.
 */

import { identifyProducer, CATEGORY_LABEL } from './pdf/producers.ts';
import { parsePdfDate } from './pdf/dates.ts';
import { describeChange } from './pdf/signatures.ts';
import type { Analysis } from './types.ts';

const GENERATOR = 'peelback — https://peelback.benrichardson.dev';

function esc(text: string): string {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function dateText(raw: string | null): string {
  const parsed = parsePdfDate(raw);
  if (!parsed) return '';
  return parsed.epochMs === null ? parsed.raw : parsed.display;
}

/* ── changes.csv ───────────────────────────────────────────────────── */

export function buildCsv(analysis: Analysis): string {
  const rows: string[][] = [
    [
      'save', 'bytes_total', 'bytes_added', 'pages', 'index_kind', 'objects_changed',
      'what_changed', 'producer', 'producer_identified', 'creator', 'created', 'modified',
      'reparsed_on_its_own', 'opened_by_a_second_engine',
    ],
  ];

  for (const r of analysis.revisions) {
    const fact = identifyProducer(r.info.producer);
    rows.push([
      String(r.index),
      String(r.byteLength),
      String(r.addedBytes),
      String(r.pageCount),
      r.kind === 'stream' ? 'cross-reference stream' : 'cross-reference table',
      String(r.changedObjectCount),
      r.changes.map(describeChange).join('; '),
      r.info.producer ?? '',
      fact ? fact.label : '',
      r.info.creator ?? '',
      dateText(r.info.creationDate),
      dateText(r.info.modDate),
      r.structureOk ? 'yes' : `no — ${r.structureError ?? 'unknown'}`,
      r.renderOk === null
        ? `not checked${r.renderError ? ` — ${r.renderError}` : ''}`
        : r.renderOk
          ? 'yes'
          : `rejected — ${r.renderError ?? 'unknown'}`,
    ]);
  }

  const header = [
    `# ${GENERATOR}`,
    `# file: ${analysis.fileName}`,
    `# ${analysis.fileBytes} bytes, saved ${analysis.saveCount} time(s)`,
    '# Saving a PDF again appends to it, so each row is the file as it stood at that save.',
    '# Multiple saves is the ordinary case. Signing, form filling and annotating each add one.',
    '# This describes a file. It is not an assessment of whether a document is genuine.',
  ].join('\n');

  return `${header}\n${rows.map((r) => r.map(csvCell).join(',')).join('\n')}\n`;
}

/* ── report.html ───────────────────────────────────────────────────── */

const STYLE = `
  :root { color-scheme: light; }
  * { box-sizing: border-box; }
  body { margin: 0; padding: 2.5rem 1.25rem 4rem; background: #faf9f7; color: #1b1a17;
         font: 15px/1.65 -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; }
  main { max-width: 46rem; margin: 0 auto; }
  h1 { font-size: 1.6rem; letter-spacing: -0.02em; margin: 0 0 0.35rem; }
  h2 { font-size: 1.05rem; margin: 2.5rem 0 0.75rem; padding-bottom: 0.4rem; border-bottom: 1px solid #e2ded6; }
  h3 { font-size: 0.95rem; margin: 1.5rem 0 0.4rem; }
  p, li { color: #45423c; }
  .sub { color: #6d6961; margin: 0 0 2rem; font-size: 0.9rem; }
  .caveat { border: 1px solid #d8d3c8; border-left: 3px solid #8a6d3b; background: #fdfaf3;
            border-radius: 8px; padding: 1.1rem 1.25rem; margin: 0 0 2rem; }
  .caveat h2 { margin-top: 0; border: 0; padding: 0; font-size: 1rem; }
  .caveat p { margin: 0.5rem 0 0; font-size: 0.92rem; }
  .headline { border: 1px solid #d8d3c8; border-radius: 8px; padding: 1.25rem; background: #fff; margin-bottom: 1.5rem; }
  .headline strong { font-size: 1.15rem; }
  table { width: 100%; border-collapse: collapse; font-size: 0.86rem; margin: 0.5rem 0 1rem; }
  th, td { text-align: left; padding: 0.5rem 0.65rem; border-bottom: 1px solid #e6e2da; vertical-align: top; }
  th { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.06em; color: #6d6961; }
  code, .mono { font-family: ui-monospace, "SF Mono", Consolas, monospace; font-size: 0.85em; }
  .wrap { overflow-x: auto; }
  ul { padding-left: 1.25rem; }
  footer { margin-top: 3rem; padding-top: 1rem; border-top: 1px solid #e2ded6; color: #6d6961; font-size: 0.82rem; }
  .rev { border: 1px solid #e2ded6; border-radius: 8px; padding: 1rem 1.15rem; margin-bottom: 0.85rem; background: #fff; }
  .rev-head { display: flex; justify-content: space-between; gap: 1rem; flex-wrap: wrap; font-weight: 600; }
  .rev dl { display: grid; grid-template-columns: max-content 1fr; gap: 0.15rem 1rem; margin: 0.6rem 0 0; font-size: 0.86rem; }
  .rev dt { color: #6d6961; }
  .rev dd { margin: 0; }
  @media print { body { background: #fff; padding: 0; } .caveat { break-inside: avoid; } }
`;

const CAVEAT_BLOCK = `
  <div class="caveat">
    <h2>Read this before anything else</h2>
    <p>
      This is a description of one file's internal structure. It is <strong>not</strong> an assessment
      of whether a document is genuine, and it is <strong>not</strong> evidence that anybody did
      anything wrong. Please do not send it to anyone as an accusation.
    </p>
    <p>
      <strong>Multiple saved versions is the ordinary case.</strong> Signing a document, filling in a
      form field, adding a comment, or a reader adding validation data by itself each add one. Of six
      PDFs published by the IRS, HUD and the US Government Publishing Office that were measured while
      the tool that produced this report was built, five carry more than one end-of-file marker, and in
      each of those five at least one marker is not an earlier version at all. Where a real earlier
      version was there, it was a digital signature that had put it there — not anybody editing the
      document.
    </p>
    <p>
      <strong>One saved version proves nothing either.</strong> Printing a document, exporting it,
      choosing “Save As”, or passing it through almost any free online PDF tool produces a brand-new
      file with no history in it at all.
    </p>
    <p>
      <strong>If something here concerns you, ask the organisation that issued the document.</strong>
      A payroll department can confirm a payslip; a bank can send a statement directly or share it
      through open banking. A file's bytes cannot tell you whether the numbers in it are true.
    </p>
  </div>
`;

export function buildReportHtml(analysis: Analysis, generatedAt: string): string {
  const a = analysis;
  const rows = a.revisions
    .map((r) => {
      const fact = identifyProducer(r.info.producer);
      const producerLine = r.info.producer
        ? `${esc(r.info.producer)}${fact ? ` — ${esc(fact.label)} (${esc(CATEGORY_LABEL[fact.category])})` : ''}`
        : 'not recorded';
      const checks = [
        r.structureOk ? 're-read on its own: yes' : `re-read on its own: no — ${esc(r.structureError ?? '')}`,
        r.renderOk === null
          ? `opened by a second engine: not checked${r.renderError ? ` — ${esc(r.renderError)}` : ''}`
          : r.renderOk
            ? 'opened by a second engine: yes'
            : `opened by a second engine: REJECTED — ${esc(r.renderError ?? '')}`,
      ].join(' · ');

      return `
        <div class="rev">
          <div class="rev-head">
            <span>Save ${r.index} of ${a.saveCount}</span>
            <span class="mono">${formatBytes(r.byteLength)}${
              r.index > 1 ? ` (${formatBytes(r.addedBytes)} added)` : ''
            } · ${r.pageCount} page${r.pageCount === 1 ? '' : 's'}</span>
          </div>
          <dl>
            <dt>This save</dt><dd>${esc(r.changes.map(describeChange).join('; '))}</dd>
            <dt>Written by</dt><dd>${producerLine}</dd>
            ${r.info.creator ? `<dt>Created in</dt><dd>${esc(r.info.creator)}</dd>` : ''}
            ${r.info.creationDate ? `<dt>Created</dt><dd>${esc(dateText(r.info.creationDate))}</dd>` : ''}
            ${r.info.modDate ? `<dt>Modified</dt><dd>${esc(dateText(r.info.modDate))}</dd>` : ''}
            <dt>Index</dt><dd>${r.kind === 'stream' ? 'cross-reference stream' : 'cross-reference table'}, ${r.changedObjectCount} object${r.changedObjectCount === 1 ? '' : 's'} written</dd>
            <dt>Checks</dt><dd>${checks}</dd>
          </dl>
        </div>`;
    })
    .join('');

  const signatures = a.signatures.length
    ? `<h2>Digital signatures</h2>${a.signatures
        .map(
          (s) => `
          <div class="rev">
            <div class="rev-head"><span>${esc(
              s.total > 1 ? `Signature ${s.order} of ${s.total}` : 'Signature',
            )}</span><span class="mono">${esc(s.subFilter ?? 'format not recorded')}</span></div>
            <dl>
              ${s.signerName ? `<dt>Name given</dt><dd>${esc(s.signerName)}</dd>` : ''}
              ${s.signedAt ? `<dt>Time given</dt><dd>${esc(dateText(s.signedAt))}</dd>` : ''}
              ${s.reason ? `<dt>Reason given</dt><dd>${esc(s.reason)}</dd>` : ''}
              ${s.location ? `<dt>Place given</dt><dd>${esc(s.location)}</dd>` : ''}
              <dt>Covered range</dt><dd class="mono">${s.byteRange ? esc(`[${s.byteRange.join(' ')}]`) : 'none written'}</dd>
              <dt>Signature value</dt><dd>${formatBytes(s.signatureValueBytes)} sitting between the two covered spans. This is the signature itself, not concealed content.</dd>
              <dt>After it</dt><dd>${esc(s.afterDescription)}</dd>
            </dl>
          </div>`,
        )
        .join('')}
        <p>
          peelback reads the ranges a signature declares and compares them against the file. It does
          <strong>not</strong> check the certificate, and it cannot tell you whether a signature is
          valid or who really holds the key. A PDF reader with the right trust settings can.
        </p>`
    : '';

  const xmpHistory = a.xmp.history.length
    ? `<h2>The record the file keeps of itself</h2>
       <p>
         Some applications embed a history of each save. It is written by the application, it can be
         incomplete, and most files have none at all — so its absence means nothing.
       </p>
       <div class="wrap"><table>
         <thead><tr><th>When</th><th>What</th><th>Software</th><th>Changed</th></tr></thead>
         <tbody>${a.xmp.history
           .map(
             (e) =>
               `<tr><td class="mono">${esc(e.when ?? '')}</td><td>${esc(e.action ?? '')}</td><td>${esc(
                 e.softwareAgent ?? '',
               )}</td><td class="mono">${esc(e.changed ?? '')}</td></tr>`,
           )
           .join('')}</tbody>
       </table></div>`
    : '';

  const findings = a.findings.length
    ? `<h2>Notes on this file</h2><ul>${a.findings
        .map((f) => `<li><strong>${esc(f.title)}.</strong> ${esc(f.detail)}</li>`)
        .join('')}</ul>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>What is inside ${esc(a.fileName)}</title>
<style>${STYLE}</style>
</head>
<body>
<main>
  <h1>What is inside ${esc(a.fileName)}</h1>
  <p class="sub">
    ${formatBytes(a.fileBytes)} · PDF ${esc(a.pdfVersion ?? 'version not recorded')} · read on ${esc(generatedAt)}
    · produced by ${GENERATOR}
  </p>

  ${CAVEAT_BLOCK}

  <div class="headline">
    <strong>This file was saved ${a.saveCount} time${a.saveCount === 1 ? '' : 's'}.</strong>
    <p>
      ${
        a.saveCount === 1
          ? 'There is one cross-reference section, so no earlier version of the document is present in these bytes. That does not mean it was never changed — re-saving a PDF in most ways leaves no earlier version behind.'
          : `Each earlier version is still physically present in the file and can be opened on its own. ${
              a.eofMarkerCount > a.saveCount
                ? `For contrast: counting “%%EOF” markers in a text editor — the method most articles recommend — would have said ${a.eofMarkerCount}.`
                : ''
            }`
      }
    </p>
  </div>

  <h2>Each saved version</h2>
  ${rows}

  ${signatures}
  ${xmpHistory}
  ${findings}

  ${
    a.attachments.length
      ? `<h2>Files attached to this document</h2><ul>${a.attachments
          .map((n) => `<li class="mono">${esc(n)}</li>`)
          .join('')}</ul>
         <p>Anything inside an attachment belongs to that file, not to this one, and was not read as this document's history.</p>`
      : ''
  }

  <h2>What this cannot tell you</h2>
  <ul>
    <li><strong>Whether the document is genuine.</strong> No reading of a file's bytes can establish that.</li>
    <li><strong>Whether anything was changed.</strong> A change made by printing, exporting or “Save As” leaves nothing here to find.</li>
    <li><strong>Who did anything.</strong> Producer and Creator are free text an application writes about itself, and the last thing to save the file replaces them.</li>
    <li><strong>Whether a signature is valid.</strong> peelback reads the ranges a signature declares. It does not check certificates.</li>
  </ul>

  <h2>The one thing worth doing next</h2>
  <p>
    Ask the organisation that issued the document. A payroll department can confirm a payslip; a bank
    can send a statement directly, or share it through open banking. That answers the question. This
    report does not.
  </p>

  <footer>
    Produced entirely in a web browser. The file described here was never uploaded anywhere.
    ${GENERATOR}
  </footer>
</main>
</body>
</html>
`;
}

/* ── the applicant's copy ──────────────────────────────────────────── */

export function buildApplicantHtml(analysis: Analysis, generatedAt: string): string {
  const a = analysis;
  const chain = a.revisions
    .map((r, i) => {
      const fact = identifyProducer(r.info.producer);
      return `<li>Save ${i + 1}: ${esc(
        r.changes.map(describeChange).join('; '),
      )}${r.info.producer ? `, written by ${esc(fact?.label ?? r.info.producer)}` : ''}${
        r.info.modDate ? ` on ${esc(dateText(r.info.modDate))}` : ''
      }.</li>`;
    })
    .join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>About a document you sent</title>
<style>${STYLE}</style>
</head>
<body>
<main>
  <h1>About a document you sent</h1>
  <p class="sub">Concerning <span class="mono">${esc(a.fileName)}</span> · read on ${esc(generatedAt)}</p>

  <div class="caveat">
    <h2>Why you have been given this</h2>
    <p>
      Someone looked at the internal structure of a PDF you sent them, using a free tool that runs in a
      web browser. This page tells you exactly what that tool showed them, so that you can see the same
      thing and answer it if you want to. Your file was not uploaded anywhere and nobody else has a copy
      of this.
    </p>
    <p>
      <strong>None of this says you did anything wrong.</strong> Everything below has ordinary
      explanations, and the most common finding — that a file has been saved more than once — is true of
      most official documents, including forms published by government agencies.
    </p>
  </div>

  <h2>What it found</h2>
  <p>
    This file has been saved ${a.saveCount} time${a.saveCount === 1 ? '' : 's'}. A PDF does not get
    rewritten when it is saved again — the changes are added to the end — so each earlier version is
    still inside the file.
  </p>
  <ul>${chain}</ul>

  <h2>Reasons a document is saved more than once</h2>
  <ul>
    <li>Someone signed it digitally. Signing is a save, and so is every countersignature.</li>
    <li>Someone filled in a form field, ticked a box, or typed a date.</li>
    <li>Someone added a comment or a note.</li>
    <li>You opened it in a PDF reader that added validation data to a signature by itself.</li>
    <li>You rotated a page, or opened it in Preview on a Mac and saved.</li>
    <li>Your employer's or bank's system generated it in stages.</li>
  </ul>

  <h2>What you can do</h2>
  <p>
    If you would like to settle it, the strongest thing is to have the document come from the source:
    ask your payroll department to send it again directly, or share the statement from your bank's own
    app or through open banking. That is better evidence than anything a file's structure can provide,
    for you as much as for them.
  </p>
  <p>
    If you removed something from the document before sending it — a private figure, someone else's
    name — say so. That is a perfectly reasonable thing to have done, and it explains a save.
  </p>

  <footer>
    Produced by ${GENERATOR}, which runs entirely in a browser and uploads nothing.
  </footer>
</main>
</body>
</html>
`;
}
